import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Chip } from '@c2fo/liquidity';
import { LoadingOverlay } from '../LoadingSpinner';
import { UriLink } from '../UriLink';
import { BackLink } from '../BackLink';
import { Card } from '../Card';
import { CardHeader } from '../CardHeader';
import { CardSection } from '../CardSection';
import { DetailField } from '../DetailField';
import { FieldsContainer } from '../FieldsContainer';
import { NotFoundCard } from '../NotFoundCard';
import { PageContainer } from '../PageContainer';
import { useDeliveriesState } from '../../contexts';
import { getStateChipType, getStateLabel } from '../../utils';
import { formatDateWithTimezone } from '../../../../utils/dateFormat';

export const DeliveryView: React.FC = () => {
  const { deliveryName } = useParams<{ deliveryName: string }>();
  const {
    currentDelivery: delivery,
    isLoadingDelivery: isLoading,
    fetchDelivery,
    clearCurrentDelivery,
  } = useDeliveriesState();

  useEffect(() => {
    if (deliveryName) {
      // Construct full path: deliveries/{deliveryName}
      fetchDelivery(`deliveries/${deliveryName}`);
    }

    return () => {
      clearCurrentDelivery();
    };
  }, [deliveryName, fetchDelivery, clearCurrentDelivery]);

  if (isLoading) {
    return <LoadingOverlay message="Loading delivery..." />;
  }

  if (!delivery) {
    return (
      <PageContainer>
        <BackLink to="/file-delivery/deliveries" label="Back to Deliveries" />
        <NotFoundCard message="Delivery not found or could not be loaded." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackLink to="/file-delivery/deliveries" label="Back to Deliveries" />

      <Card>
        <CardHeader
          chip={
            <Chip
              label={getStateLabel(delivery.state)}
              type={getStateChipType(delivery.state)}
              variant="outlined"
              size="xs"
            />
          }
          title={delivery.scheduledDelivery ? 'Scheduled Delivery' : 'Immediate Delivery'}
        />
        {delivery.statusMessage && (
          <div className="px-6 pb-4">
            <div className="text-sm font-medium text-gray-500 mb-1">Status Message</div>
            <div className="text-sm text-gray-900">{delivery.statusMessage}</div>
          </div>
        )}
        <FieldsContainer>
          <DetailField label="Source Location" value={delivery.sourceLocation ?? 'Unknown'} mono />
          <DetailField label="Target Location" value={delivery.targetLocation ?? 'Unknown'} mono />
          <DetailField label="Resource Name" value={delivery.name} mono />
          <DetailField
            label="Start Time"
            value={delivery.startTime ? formatDateWithTimezone(delivery.startTime) : undefined}
          />
          <DetailField
            label="End Time"
            value={delivery.endTime ? formatDateWithTimezone(delivery.endTime) : undefined}
          />
        </FieldsContainer>
        {delivery.fileTransfers && delivery.fileTransfers.length > 0 && (
          <CardSection title={`Transferred Files (${delivery.fileTransfers.length})`}>
            <div className="max-h-48 overflow-y-auto border border-gray-200 rounded bg-gray-50 p-2">
              <ul className="text-sm text-gray-900 font-mono space-y-1">
                {delivery.fileTransfers.map((transfer, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <span className={transfer.success ? 'text-green-600' : 'text-red-600'}>
                      {transfer.success ? '✓' : '✗'}
                    </span>
                    <UriLink uri={transfer.targetUri} truncate={false} showTooltip={false} />
                  </li>
                ))}
              </ul>
            </div>
          </CardSection>
        )}
      </Card>
    </PageContainer>
  );
};
